import React, { useCallback, useEffect, useState } from 'react';
import { brandService, partyPatronService } from '../../services';
import { useUser } from '../../context/UserContext';
import { useDialog } from '../../context/DialogContext';
import { AdminPageHeader, AreaBar, Btn, DataTable, Field, HardCard, inputClass, shortDate, useAdminShell } from '../../components/admin/shell';

const areaNote = (a) => {
  if (!a.area) return 'Search a city — patrons are scheduled per area.';
  if (a.resolving) return 'Resolving area…';
  if (!a.region) return 'No area covers that city yet — it gets created the first time something there is featured.';
  return `Scheduling for ${a.region.name || a.region.slug}.`;
};

const statusOf = (p) => (p.dispatchedAt ? 'DISPATCHED' : String(p.slotStatus || '').toUpperCase());

/**
 * MARKETING › PARTY PATRONS — spec/sections.md §16. One brand per day, per area. The list
 * is scoped to the AREA bar's region; `+ SCHEDULE A PATRON` (create) opens the form as a
 * modal and needs a resolved region; ACTIVATE / CANCEL (edit) follow today's rules and
 * vanish once the slot has dispatched.
 */
export default function PatronsPage() {
  const { can } = useUser();
  const { showAlert } = useDialog();
  const { area } = useAdminShell();
  const canCreate = can('patrons', 'create');
  const canEdit = can('patrons', 'edit');
  const regionId = area?.region?.id || null;
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [paying, setPaying] = useState(null);

  const load = useCallback(async () => {
    const r = await partyPatronService.list({ regionId });
    setRows(r.success ? (r.data || []) : []);
  }, [regionId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (p, status) => {
    const r = await partyPatronService.setStatus(p.id, status);
    if (!r.success) await showAlert({ title: 'COULD NOT UPDATE', message: r.error || 'Try again.', type: 'error', theme: 'admin' });
    load();
  };

  const actions = (p) => {
    if (p.dispatchedAt) return [];
    return [
      p.slotStatus !== 'active' && { label: 'ACTIVATE', variant: 'solid', onClick: () => setStatus(p, 'active') },
      p.slotStatus !== 'canceled' && { label: 'CANCEL', onClick: () => setStatus(p, 'canceled') },
      canEdit && { label: p.paidAmountCents > 0 ? 'EDIT PAID' : 'SET PAID', onClick: () => setPaying(p) },
    ].filter(Boolean);
  };

  const count = (rows || []).length;
  return (
    <>
      <AdminPageHeader
        section="patrons"
        meta="One brand per day, per area."
        actions={canCreate ? <Btn variant="white" onClick={() => setOpen(true)}>+ SCHEDULE A PATRON</Btn> : null}
      />
      <AreaBar note={areaNote} />
      <div className="flex flex-col gap-4">
        <DataTable
          columns={[
            { key: 'brandName', label: 'BRAND', fr: 1.8 },
            { key: 'regionName', label: 'AREA', fr: 1.6, render: (p) => p.regionName || p.regionSlug || '—' },
            { key: 'dayActive', label: 'DAY', fr: 1, render: (p) => shortDate(p.dayActive) },
            { key: 'slotStatus', label: 'STATUS', fr: 1, render: statusOf },
            // Impressions are guests who opened the gift notification; clicks are taps on
            // the brand's link from it. A dispatched day with zeros is a real answer.
            { key: 'opens', label: 'IMPRESSIONS', fr: 0.9, right: true, render: (p) => (p.dispatchedAt ? (p.opens ?? 0).toLocaleString() : '—') },
            { key: 'clicks', label: 'CLICKS', fr: 0.8, right: true, render: (p) => (p.dispatchedAt ? (p.clicks ?? 0).toLocaleString() : '—') },
            { key: 'giftsCredited', label: 'GIFTS', fr: 0.9, right: true, render: (p) => (p.dispatchedAt ? `${(p.giftsSpent ?? 0).toLocaleString()} / ${(p.giftsCredited ?? 0).toLocaleString()}` : '—') },
          ]}
          rows={rows || []}
          loading={rows == null}
          hasActions={canEdit}
          actions={actions}
          empty={regionId ? 'NO PATRON DAYS HERE YET.' : 'NOTHING HERE YET.'}
          footerLeft={`${count} patron ${count === 1 ? 'day' : 'days'}`}
        />
        <p className="font-mono text-[11px] leading-[1.6] text-[#6B7280]">EVENT SPONSORS — brands a host attaches to one event — have no page yet. They get one the day it ships.</p>
      </div>
      {open && <ScheduleModal region={area?.region} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {paying && <PaidModal patron={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); load(); }} />}
    </>
  );
}

function PaidModal({ patron, onClose, onSaved }) {
  const [value, setValue] = useState(patron.paidAmountCents ? (patron.paidAmountCents / 100).toFixed(2) : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const cents = Math.round(parseFloat(value) * 100);
    if (!Number.isFinite(cents) || cents <= 0) { setError('Enter what the brand paid.'); return; }
    setBusy(true); setError('');
    const r = await partyPatronService.setPaid(patron.id, cents);
    setBusy(false);
    if (r.success) onSaved(); else setError(r.error || 'Could not save.');
  };
  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-16" onClick={onClose}>
      <HardCard className="w-full max-w-[420px]" onClick={(e) => e.stopPropagation()}>
        <div onClick={(e) => e.stopPropagation()} className="flex flex-col gap-4 p-4">
          <div className="px-2 pt-2 font-mono text-[14px] font-bold uppercase tracking-[0.14em]">BRAND PAID</div>
          <Field label="BRAND PAID ($)">
            <input inputMode="decimal" data-testid="patron-paid-edit" value={value} onChange={(e) => setValue(e.target.value.replace(/[^\d.]/g, ''))} className={inputClass} placeholder="0.00" />
          </Field>
          <div className="font-mono text-[10px] text-[#6B7280]">Gifts are paid only if they fit inside this amount.</div>
          {error && <div className="font-mono text-[11px] text-red-600">{error}</div>}
          <div className="flex justify-end gap-2">
            <Btn onClick={onClose}>CANCEL</Btn>
            <Btn variant="solid" onClick={save} disabled={busy}>{busy ? 'SAVING…' : 'SAVE'}</Btn>
          </div>
        </div>
      </HardCard>
    </div>
  );
}

function ScheduleModal({ region, onClose, onSaved }) {
  const [brands, setBrands] = useState([]);
  const [form, setForm] = useState({ brandId: '', dayActive: '', customMessage: '', promoCode: '', promoLink: '', paid: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { (async () => { const r = await brandService.list(); if (r.success) setBrands(r.data || []); })(); }, []);

  const save = async () => {
    if (!form.brandId || !form.dayActive || !region?.id) { setError('Brand, area, and day are required — pick a city above.'); return; }
    // The gifts are paid out of Portal's own cash, so they can never exceed what the brand paid
    // for the day: the server pays nobody when they would (payments audit H10).
    const paidCents = Math.round(parseFloat(form.paid) * 100);
    if (!Number.isFinite(paidCents) || paidCents <= 0) { setError('Enter what the brand paid for this day — gifts can never exceed it.'); return; }
    setBusy(true); setError('');
    const r = await partyPatronService.schedule({
      brandId: form.brandId,
      regionId: region.id,
      dayActive: form.dayActive,
      customMessage: form.customMessage || undefined,
      promoCode: form.promoCode || undefined,
      promoLink: form.promoLink || undefined,
      paidAmountCents: paidCents,
    });
    setBusy(false);
    if (r.success) onSaved(); else setError(r.error || 'Could not schedule this patron.');
  };

  return (
    <div className="fixed inset-0 safe-inset z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-16" onClick={onClose}>
      <HardCard className="w-full max-w-[560px]" onClick={(e) => e.stopPropagation()}>
        <div onClick={(e) => e.stopPropagation()}>
          <div className="px-6 pb-2 pt-6 font-mono text-[14px] font-bold uppercase tracking-[0.14em]">SCHEDULE A PATRON</div>
          <div className="flex flex-col gap-4 p-4">
            <div className="font-mono text-[11px] text-[#6B7280]">{region ? `Area: ${region.name || region.slug}` : 'No area resolved — pick a city in the AREA bar first.'}</div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="BRAND">
                <select value={form.brandId} onChange={(e) => set('brandId', e.target.value)} className={inputClass}>
                  <option value="">— select —</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="DAY"><input type="date" value={form.dayActive} onChange={(e) => set('dayActive', e.target.value)} className={inputClass} /></Field>
            </div>
            <Field label="BRAND PAID ($)">
              <input inputMode="decimal" data-testid="patron-paid" value={form.paid} onChange={(e) => set('paid', e.target.value.replace(/[^\d.]/g, ''))} className={inputClass} placeholder="0.00" />
              <div className="mt-1 font-mono text-[10px] text-[#6B7280]">Gifts ($5 per guest, $10 per host by default) are paid only if they fit inside this amount.</div>
            </Field>
            <Field label="CUSTOM MESSAGE (MAX 200)"><textarea rows={2} value={form.customMessage} onChange={(e) => set('customMessage', e.target.value.slice(0, 200))} className={inputClass} /></Field>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field label="PROMO CODE (OPTIONAL)"><input value={form.promoCode} onChange={(e) => set('promoCode', e.target.value)} className={inputClass} /></Field>
              <Field label="PROMO LINK (OPTIONAL)"><input value={form.promoLink} onChange={(e) => set('promoLink', e.target.value)} className={inputClass} placeholder="https://" /></Field>
            </div>
            {error && <div className="font-mono text-[11px] text-red-600">{error}</div>}
            <div className="flex flex-wrap justify-end gap-2 border-t border-[#F2F2F2] pt-4">
              <Btn onClick={onClose}>CANCEL</Btn>
              <Btn variant="solid" onClick={save} disabled={busy}>{busy ? 'SCHEDULING…' : 'SCHEDULE'}</Btn>
            </div>
          </div>
        </div>
      </HardCard>
    </div>
  );
}
